// components
import Nav from "Shared/ts/components/nav";
import FingerPrintNav from "Shared/ts/components/fingerprint-nav";
import SocialProofElement from "Shared/ts/components/social-proof";

// utils
import Flyout from "Shared/ts/utils/flyout";

declare global {
    interface Window {
        flyoutRepository: WeakMap<HTMLElement, Flyout>;
    }
}

export const initializeGlobalFlyoutRepository = (): WeakMap<
    HTMLElement,
    Flyout
> => {
    const flyoutRepository = new WeakMap<HTMLElement, Flyout>();

    window.flyoutRepository = flyoutRepository;

    return flyoutRepository;
};

export const initializeFlyouts = (selector: string): void => {
    const elements = Array.from(
        document.querySelectorAll(selector)
    ) as HTMLElement[];

    elements.forEach((element) => {
        const flyout = new Flyout(element);

        if ("flyoutRepository" in window) {
            window.flyoutRepository.set(element, flyout);
        }
    });
};

export const initializeNavById = (id: string): Nav | undefined => {
    const element = document.getElementById(id);
    if (!element) return;

    return new Nav(element.id);
};

export const initializeFlyoutsThenNav = (
    selector: string,
    id: string
): void => {
    initializeFlyouts(selector);
    initializeNavById(id);
};

const getScrollableHeightByElement = (
    element: HTMLElement,
    callback: (height: number) => void
): void => {
    const o = new ResizeObserver((entries) => {
        const height = entries
            .filter((entry) => entry.target === element)
            .map((entry) => entry.contentRect.height)
            .reduce((a, b) => (a > b ? a : b), 0);

        callback(height);
    });

    o.observe(element);
};

/**
 * Takes an HTMLElement, representing the scrollable content, and auto-updates the height of it to a CSS variable.
 * @param element HTMLElement | undefined | null
 * @returns void
 */
export const importScrollableHeightByElement = (
    element: HTMLElement | undefined | null,
    customPropertyName: string = "scrollable-content-height"
): void => {
    if (!element) return;

    const canAdoptStyleSheets = "adoptedStyleSheets" in document;
    if (canAdoptStyleSheets) {
        const stylesheet = new CSSStyleSheet();

        getScrollableHeightByElement(element, (height: number) => {
            stylesheet.replaceSync(
                `:root { --${customPropertyName}: ${height}px; }`
            );

            document.adoptedStyleSheets = [
                ...document.adoptedStyleSheets,
                stylesheet
            ];
        });

        return;
    }

    importScrollableHeightByElementIntoStyleElement(
        element,
        customPropertyName
    );
};

export const importScrollableHeightByElementIntoStyleElement = (
    element: HTMLElement | undefined | null,
    customPropertyName: string = "scrollable-content-height"
): void => {
    if (!element) return;

    const style = document.createElement("style");
    getScrollableHeightByElement(element, (height: number) => {
        style.innerHTML = `:root { --${customPropertyName}: ${height}px; }`;
    });

    document.body.appendChild(style);
};

/**
 * Takes a `FingerPrintNav` instance along with an `<social-proof>` component and controls the visibility of the `FingerPrintNav` once the `<social-proof>` component displays onscreen.
 * @param fpn FingerPrintNav
 * @param spe SocialProofElement
 * @returns FingerPrintNav
 */
export const initializeFingerPrintNavBySocialProofElement = (
    fpn = new FingerPrintNav(),
    spe = document.querySelector<SocialProofElement>("social-proof")
): FingerPrintNav => {
    fpn.hide();

    if (!spe) {
        fpn.show();

        return fpn;
    }

    spe?.addEventListener(
        "open",
        (event) => {
            fpn.show();
        },
        {
            once: true
        }
    );

    return fpn;
};
